import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowRight, FaInstagram, FaFacebookF } from "react-icons/fa";
import ForgeLogo from "../logo";
import "./Navbar.css";

/* ── Fullscreen menu links ─────────────────────────────────── */
const MENU_LINKS = [
  { num: "01", label: "Who We Serve", to: "/#who-we-serve" },
  { num: "02", label: "How It Works", to: "/#retreat" },
  { num: "03", label: "Forge Ecosystem", to: "/#research" },
  { num: "04", label: "About", to: "/#about" },
  { num: "05", label: "Community", to: "/community" },
  { num: "06", label: "Apply", to: "/apply" },
  { num: "07", label: "Support", to: "/donate" },
  { num: "08", label: "Contact", to: "/contact" },
];

const SOCIALS = [
  { Icon: FaInstagram, href: "https://www.instagram.com/forgengo", label: "Instagram" },
  { Icon: FaFacebookF, href: "https://www.facebook.com/groups/1713906612814894/", label: "Facebook" },
  // { Icon: href: "#youtube", label: "YouTube" },
];

const ease = [0.22, 1, 0.36, 1];

/* ── Animation variants ────────────────────────────────────── */
const overlayVariants = {
  closed: { opacity: 0, transition: { duration: 0.35, ease } },
  open: { opacity: 1, transition: { duration: 0.4, ease } },
};

const panelVariants = {
  closed: { x: "100%", transition: { duration: 0.45, ease } },
  open: { x: "0%", transition: { duration: 0.5, ease } },
};

const linkVariants = {
  closed: { opacity: 0, x: 30 },
  open: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease, delay: 0.1 + i * 0.07 },
  }),
};

const footerVariants = {
  closed: { opacity: 0, y: 12 },
  open: { opacity: 1, y: 0, transition: { duration: 0.4, ease, delay: 0.55 } },
};

/* ── Hamburger icon ────────────────────────────────────────── */
function HamburgerIcon({ open }) {
  return (
    <span className="fg-nav__burger" aria-hidden="true">
      <span className={`fg-nav__burger-line ${open ? "top-open" : ""}`} />
      <span className={`fg-nav__burger-line ${open ? "btm-open" : ""}`} />
    </span>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  /* Close menu on route change */
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  /* Lock body scroll when menu is open */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const toggle = () => setOpen((v) => !v);
  const close = () => setOpen(false);

  return (
    <>
      {/* ── Slim top bar (always visible) ── */}
      <motion.header className="fg-nav" initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
        <div className="fg-nav__bar container-fg container-2xxl">
          {/* Brand */}
          <Link to="/" className="fg-nav__brand" onClick={close}>
            <ForgeLogo fill="var(--c-rust)" width={42} height={42} />
            <span className="fg-nav__wordmark">
              FORGE
              <br />
              {/* <span className="fg-nav__sub-word">NGO</span> */}
            </span>
          </Link>

          {/* Right controls */}
          <div className="fg-nav__controls">
            <Link to="/apply" className="fg-nav__apply" onClick={close}>
              Apply
            </Link>
            <Link to="/donate" className="fg-nav__donate-btn" onClick={close}>
              Donate
            </Link>
            {/* Member portal — sign in or create an account (open registration) */}
            <Link to="/portal/login" className="fg-nav__signin-btn" onClick={close}>
              Sign In
            </Link>
            <button type="button" className="fg-nav__menu-btn" onClick={toggle} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
              <HamburgerIcon open={open} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* ── Fullscreen overlay ── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Dark backdrop */}
            <motion.div className="fg-menu__backdrop" variants={overlayVariants} initial="closed" animate="open" exit="closed" onClick={close} />

            {/* Slide-in panel */}
            <motion.div className="fg-menu" role="dialog" aria-label="Site navigation" aria-modal="true" variants={panelVariants} initial="closed" animate="open" exit="closed">
              {/* Menu header */}
              <div className="fg-menu__head">
                <Link to="/" className="fg-menu__brand" onClick={close}>
                  <ForgeLogo fill="var(--c-rust)" width={40} height={40} />
                  <span className="fg-menu__wordmark">
                    FORGE
                    <br />
                    <span className="fg-menu__sub">RETREAT</span>
                  </span>
                </Link>

                <button type="button" className="fg-menu__close" onClick={close} aria-label="Close menu">
                  <span className="fg-menu__close-label">CLOSE</span>
                  <span className="fg-menu__close-x" aria-hidden="true">
                    ✕
                  </span>
                </button>
              </div>

              {/* Nav links */}
              <nav className="fg-menu__nav" aria-label="Main navigation">
                {MENU_LINKS.map((l, i) => (
                  <motion.div key={l.label} className="fg-menu__item" custom={i} variants={linkVariants} initial="closed" animate="open" exit="closed">
                    <span className="fg-menu__num">{l.num}</span>
                    <NavLink to={l.to} className={({ isActive }) => `fg-menu__link${isActive ? " fg-menu__link--active" : ""}`} onClick={close} end>
                      {l.label}
                      <FaArrowRight className="fg-menu__arrow" aria-hidden="true" />
                    </NavLink>
                  </motion.div>
                ))}
              </nav>

              {/* Footer row */}
              <motion.div className="fg-menu__footer" variants={footerVariants} initial="closed" animate="open" exit="closed">
                {/* Socials */}
                <div className="fg-menu__socials">
                  <p className="fg-menu__social-label">Follow Us</p>
                  <div className="fg-menu__social-icons">
                    {SOCIALS.map(({ Icon, href, label }) => (
                      <a key={label} href={href} className="fg-menu__social-link" aria-label={label}>
                        <Icon />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Tagline */}
                <p className="fg-menu__tagline">Forge Retreat provides a path to healing and transformation for those who have served.</p>

                {/* Questions CTA */}
                <Link to="/contact" className="fg-menu__contact-cta" onClick={close}>
                  <div className="fg-menu__contact-icon" aria-hidden="true">
                    <ForgeLogo fill="var(--c-sand)" width={28} height={28} />
                  </div>
                  <div>
                    <p className="fg-menu__contact-ql">QUESTIONS?</p>
                    <p className="fg-menu__contact-gl">
                      Get in touch <FaArrowRight className="fg-menu__contact-arrow" />
                    </p>
                  </div>
                </Link>

                {/* Mobile-only CTA buttons */}
                <div className="fg-menu__mobile-ctas">
                  <Link to="/apply" className="fg-menu__mobile-apply" onClick={close}>
                    Apply
                  </Link>
                  <Link to="/donate" className="fg-menu__mobile-donate" onClick={close}>
                    Donate
                  </Link>
                  <Link to="/portal/login" className="fg-menu__mobile-apply fg-menu__mobile-signin" onClick={close}>
                    Sign In / Create Account
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
